import React, {useContext, useEffect, useRef, useState} from 'react'
import {defineMessage, useIntl, defineMessages} from 'react-intl'
import PropTypes from 'prop-types'
import {
    SimpleGrid,
    Stack,
    Grid,
    List,
    ListItem,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useAddressFields from '../../components/forms/useAddressFields'
import Field from '../../components/field'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AddressFieldsContext} from '../../contexts/address-context'
import {useAddressSuggestion} from '../../integrations/melissa/hooks/use-melissa' // Import the hook
import debounce from 'lodash/debounce'
import {useOutsideClick} from '@chakra-ui/react'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {sendClickEvent} from '../../utils/gtm-utils'

const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form'
})

const EnterAddressManuallyToggle = ({showAddressFields, onShowAddressFields}) => {
    const intl = useIntl()
    const messages = defineMessages({
        useAutocomplete: {
            id: 'address.toggle.use_autocomplete',
            defaultMessage: 'Use address autocomplete'
        },
        enterManually: {
            id: 'address.toggle.enter_manually',
            defaultMessage: 'Enter address manually'
        }
    })

    const messageDescriptor = showAddressFields ? messages.useAutocomplete : messages.enterManually

    return (
        <Text
            display={'inline-block'}
            pt={2}
            textDecor={'underline'}
            fontWeight={400}
            fontSize={'12px'}
            cursor={'pointer'}
            onClick={() => onShowAddressFields((s) => !s)}
            onKeyDown={(e) => e.key === 'Enter' && onShowAddressFields((s) => !s)?.(e)}
            role="button"
            tabIndex={0}
            aria-label={intl.formatMessage(messageDescriptor)}
        >
            {intl.formatMessage(messageDescriptor)}
        </Text>
    )
}

EnterAddressManuallyToggle.propTypes = {
    showAddressFields: PropTypes.bool,
    onShowAddressFields: PropTypes.func
}

const AddressFields = ({
    form,
    prefix = '',
    formTitleAriaLabel = defaultFormTitleAriaLabel,
    isBillingAddress = false
}) => {
    const {data: customer} = useCurrentCustomer()
    const fields = useAddressFields({form, prefix})
    const intl = useIntl()
    const addressFormRef = useRef()
    const {showAddressFields, setShowAddressFields} = useContext(AddressFieldsContext)
    const boxRef = useRef()
    const suggestionsListRef = useRef()
    const itemsRef = useRef([])

    // State for address1 input and suggestions
    const [address1, setAddress1] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    useOutsideClick({
        ref: boxRef,
        handler: () => setShowSuggestions(false)
    })

    // Initialize the useAddressSuggestion hook
    const {isLoading, suggestions, fetchSuggestions} = useAddressSuggestion(address1)

    const config = getConfig()
    const ADDRESS_SUGGESTIONS_ENABLED = config.app.ADDRESS_SUGGESTIONS_ENABLED === 'true'
    const ADDRESS_SERVICES_DEBOUNCE_INTERVAL =
        Number(config.app.ADDRESS_SERVICES_DEBOUNCE_INTERVAL) || 300
    // Debounced function to fetch suggestions
    const debouncedFetchSuggestionsRef = useRef(
        debounce((data) => {
            if (ADDRESS_SUGGESTIONS_ENABLED) {
                const {value, shouldFetch} = data
                if (value.length >= 7) {
                    fetchSuggestions({address: value, shouldFetch})
                }
            }
        }, ADDRESS_SERVICES_DEBOUNCE_INTERVAL)
    )

    // Effect to handle address1 input changes
    useEffect(() => {
        if (address1.length >= 7) {
            debouncedFetchSuggestionsRef.current({value: address1, shouldFetch: !showAddressFields})
        }
    }, [address1])

    // Sync form value to local state when form is reset or updated externally
    useEffect(() => {
        const formAddress1Value = form.getValues(fields.address1.name)
        if (formAddress1Value && formAddress1Value !== address1) {
            setAddress1(formAddress1Value)
        }
    }, [form.watch(fields.address1.name)])

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        addressFormRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (showSuggestions && highlightedIndex >= 0 && itemsRef.current[highlightedIndex]) {
            itemsRef.current[highlightedIndex].focus()
        }
    }, [highlightedIndex, showSuggestions])

    const handleAddress1Change = (e) => {
        const value = e.target.value
        setAddress1(value)
        form.setValue(fields.address1.name, value)
        setShowSuggestions(true)
    }

    const handleAddress1KeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setShowSuggestions(true)
                setHighlightedIndex(0)
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                    handleSuggestionClick(suggestions[highlightedIndex])
                }
                break
            case 'Escape':
                e.preventDefault()
                setShowSuggestions(false)
                setHighlightedIndex(-1)
                break
            default:
                break
        }
    }

    const handleSuggestionItemKeyDown = (e, index) => {
        setHighlightedIndex(index)

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault()
                handleSuggestionClick(suggestions[index])
                break
            case 'Escape':
                e.preventDefault()
                setShowSuggestions(false)
                setHighlightedIndex(-1)
                break
            case 'ArrowDown':
                e.preventDefault()
                if (index < suggestions.length - 1) {
                    setHighlightedIndex(index + 1)
                }
                break
            case 'ArrowUp':
                e.preventDefault()
                if (index > 0) {
                    setHighlightedIndex(index - 1)
                }
                break
            default:
                break
        }
    }

    const handleSuggestionClick = (suggestion) => {
        // Populate fields with the selected suggestion
        setAddress1(suggestion.address1)
        form.setValue(fields.address1.name, suggestion.address1)
        form.setValue(fields.apartment.name, suggestion.address2)
        form.setValue(fields.city.name, suggestion.city)
        form.setValue(fields.stateCode.name, suggestion.stateCode)
        form.setValue(fields.postalCode.name, suggestion.postalCode)
        form.setValue(fields.countryCode.name, 'GB')
        setShowSuggestions(false)
        setShowAddressFields(true) // Show detailed address fields
    }

    if (!ADDRESS_SUGGESTIONS_ENABLED) {
        setShowAddressFields(true)
    }

    // GTM Modification
    const sendGTMEvent = (type) => {
        if (type && !isBillingAddress) {
            sendClickEvent(type)
        }
    }

    return (
        <Stack
            spacing={4}
            aria-label={intl.formatMessage(formTitleAriaLabel)}
            tabIndex="0"
            ref={addressFormRef}
        >
            <SimpleGrid columns={[1, 1, 2]} gap={4}>
                <Field
                    hideLabel
                    inputProps={{
                        variant: 'outline'
                    }}
                    placeholder={intl.formatMessage({
                        id: 'use_address_fields.label.first_name',
                        defaultMessage: 'First Name*'
                    })}
                    onClick={() => sendGTMEvent('shipping-address_input_first-name')}
                    {...fields.firstName}
                />
                <Field
                    hideLabel
                    inputProps={{
                        variant: 'outline'
                    }}
                    placeholder={intl.formatMessage({
                        id: 'use_address_fields.label.last_name',
                        defaultMessage: 'Last Name*'
                    })}
                    onClick={() => sendGTMEvent('shipping-address_input_last-name')}
                    {...fields.lastName}
                />
            </SimpleGrid>
            <Box ref={boxRef}>
                <div>
                    <Field
                        hideLabel
                        inputProps={{
                            variant: 'outline',
                            placeholder: intl.formatMessage({
                                id: 'use_address_fields.label.street_address',
                                defaultMessage: 'Address*'
                            }),
                            value: address1,
                            onChange: handleAddress1Change,
                            onKeyDown: handleAddress1KeyDown,
                            onKeyUp: () => setShowSuggestions(true),
                            autoComplete: 'off', // Disable browser autocomplete
                            ariaAutocomplete: 'list',
                            ariaControls: showSuggestions ? 'address-suggestions' : undefined,
                            ariaExpanded: showSuggestions && suggestions.length > 0,
                            role: 'combobox'
                        }}
                        {...fields.address1}
                    />
                    {!showAddressFields && (
                        <EnterAddressManuallyToggle
                            showAddressFields={showAddressFields}
                            onShowAddressFields={setShowAddressFields}
                        />
                    )}
                </div>
                {showSuggestions &&
                    !showAddressFields &&
                    suggestions.length >= 1 &&
                    address1.length >= 7 && (
                        <List
                            id='address-suggestions'
                            ref={suggestionsListRef}
                            role="listbox"
                            spacing={0}
                            position={'absolute'}
                            bg={'white'}
                            w={'full'}
                            zIndex={1}
                            shadow={'dark-lg'}
                            borderColor={'gray.700'}
                            overflowY={'auto'}
                            aria-label={intl.formatMessage({
                                id: 'address.suggestions.aria_label',
                                defaultMessage: 'Address suggestions'
                            })}
                            css={{
                                maxHeight: 'calc(4 * 3.5rem)',
                                '@media (min-width: 768px)': {
                                    maxHeight: '152px'
                                },
                                '&::-webkit-scrollbar': {
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'grey.300',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'grey.700',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        background: 'grey.800'
                                    }
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'grey.700 grey.300'
                            }}
                        >
                            {suggestions.map((suggestion, index) => {
                                const formattedSuggestion = [
                                    suggestion.address1,
                                    suggestion.address2,
                                    suggestion.city,
                                    suggestion.stateCode,
                                    suggestion.postalCode
                                ]
                                    .filter(Boolean)
                                    .join(', ')

                                return (
                                    <ListItem
                                        key={index}
                                        ref={(el) => (itemsRef.current[index] = el)}
                                        role="option"
                                        aria-selected={index === highlightedIndex}
                                        tabIndex={index === highlightedIndex ? 0 : -1}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        onKeyDown={(e) => handleSuggestionItemKeyDown(e, index)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        py={2}
                                        px={4}
                                        fontSize={'sm'}
                                        borderColor={'cardDivider'}
                                        borderBottomWidth={
                                            index < suggestions.length - 1 ? '1px' : '0'
                                        }
                                        minHeight={['3.5rem', '3.5rem', '38px']}
                                        display={'flex'}
                                        alignItems={'center'}
                                        _hover={{bg: 'grey.300', cursor: 'pointer', border: 0}}
                                        _focus={{bg: 'grey.300', border: 0, outline: 'none'}}
                                        sx={{
                                            lineHeight: '1.4',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {formattedSuggestion}
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}

                {isLoading && <LoadingSpinner />}
            </Box>
            {showAddressFields && (
                <Field
                    hideLabel
                    {...fields.apartment}
                    inputProps={{
                        variant: 'outline',
                        placeholder: intl.formatMessage({
                            id: 'use_address_fields.label.apartment',
                            defaultMessage: 'Flat Number / House Number'
                        })
                    }}
                />
            )}
            <input
                type="hidden"
                {...form.register(fields.countryCode.name, fields.countryCode.rules)}
                value="GB"
            />
            {showAddressFields && (
                <>
                    <SimpleGrid columns={[1, 1, 2]} gap={2}>
                        <Field
                            hideLabel
                            {...fields.city}
                            inputProps={{
                                variant: 'outline',
                                placeholder: intl.formatMessage({
                                    id: 'use_address_fields.label.city',
                                    defaultMessage: 'Town / City*'
                                })
                            }}
                            onClick={() => sendGTMEvent('shipping-address_input_city')}
                        />
                        <Field
                            hideLabel
                            inputProps={{
                                variant: 'outline',
                                placeholder: intl.formatMessage({
                                    id: 'use_address_fields.label.postal_code',
                                    defaultMessage: 'Postcode*'
                                })
                            }}
                            onClick={() => sendGTMEvent('shipping-address_input_zip-code')}
                            {...fields.postalCode}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={[1, 1, 2]} gap={2}>
                        {/* <div>
                            <Field
                                hideLabel
                                inputProps={{
                                    variant: 'outline',
                                    placeholder: intl.formatMessage({
                                        id: 'use_address_fields.label.province',
                                        defaultMessage: 'Province'
                                    })
                                }}
                                onClick={() => sendGTMEvent('shipping-address_input_state')}
                                {...fields.stateCode}
                            />
                            {ADDRESS_SUGGESTIONS_ENABLED && (
                                <EnterAddressManuallyToggle
                                    showAddressFields={showAddressFields}
                                    onShowAddressFields={setShowAddressFields}
                                />
                            )}
                        </div> */}
                        <div>
                            <Field
                                hideLabel
                                inputProps={{
                                    variant: 'outline',
                                    disabled: true
                                }}
                                onClick={() => sendGTMEvent('shipping-address_input_country')}
                                {...fields.countryCode}
                            />
                            {ADDRESS_SUGGESTIONS_ENABLED && (
                                <EnterAddressManuallyToggle
                                    showAddressFields={showAddressFields}
                                    onShowAddressFields={setShowAddressFields}
                                />
                            )}
                        </div>
                    </SimpleGrid>
                </>
            )}
            <SimpleGrid columns={[1, 1, 2]} gap={2}>
                <Field
                    hideLabel
                    inputProps={{
                        variant: 'outline',
                        placeholder: intl.formatMessage({
                            id: 'use_address_fields.label.phone',
                            defaultMessage: 'Phone'
                        })
                    }}
                    onClick={() => sendGTMEvent('shipping-address_input_telephone')}
                    {...fields.phone}
                />
            </SimpleGrid>
            {customer.isRegistered && !isBillingAddress && <Field {...fields.preferred} />}
        </Stack>
    )
}

AddressFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional aria label to use for the address form */
    formTitleAriaLabel: MESSAGE_PROPTYPE,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool
}

export default AddressFields
